
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { BookOpen, Receipt, MessageSquare, FileText } from "lucide-react";

interface FeatureSettings {
  classEnrollments: boolean;
  onlinePayments: boolean;
  messaging: boolean;
  documentAccess: boolean;
}

interface ParentPortalFeaturesTabProps {
  featureSettings: FeatureSettings;
  onFeatureToggle: (feature: keyof FeatureSettings) => void;
}

const ParentPortalFeaturesTab: React.FC<ParentPortalFeaturesTabProps> = ({
  featureSettings,
  onFeatureToggle,
}) => (
  <div className="max-w-4xl">
    <Card>
      <CardHeader>
        <CardTitle>Portal Features</CardTitle>
        <CardDescription>
          Control which features are available in the parent portal
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <BookOpen className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="flex-grow">
                <Label htmlFor="class-enrollments" className="block text-left font-medium">Class Enrollments</Label>
                <p className="text-sm text-muted-foreground text-left mt-1">Allow parents to view and request class enrollments</p>
              </div>
            </div>
            <Switch 
              id="class-enrollments" 
              checked={featureSettings.classEnrollments}
              onCheckedChange={() => onFeatureToggle('classEnrollments')}
            />
          </div>
          
          <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <Receipt className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="flex-grow">
                <Label htmlFor="online-payments" className="block text-left font-medium">Online Payments</Label>
                <p className="text-sm text-muted-foreground text-left mt-1">Allow parents to make payments online</p>
              </div>
            </div>
            <Switch 
              id="online-payments" 
              checked={featureSettings.onlinePayments}
              onCheckedChange={() => onFeatureToggle('onlinePayments')}
            />
          </div>
          
          <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <MessageSquare className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="flex-grow">
                <Label htmlFor="messaging" className="block text-left font-medium">Messaging</Label>
                <p className="text-sm text-muted-foreground text-left mt-1">Enable direct messaging between parents and staff</p>
              </div>
            </div>
            <Switch 
              id="messaging" 
              checked={featureSettings.messaging}
              onCheckedChange={() => onFeatureToggle('messaging')}
            />
          </div>
          
          <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <FileText className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="flex-grow">
                <Label htmlFor="document-access" className="block text-left font-medium">Document Access</Label>
                <p className="text-sm text-muted-foreground text-left mt-1">Allow parents to access and upload documents</p>
              </div>
            </div>
            <Switch 
              id="document-access" 
              checked={featureSettings.documentAccess}
              onCheckedChange={() => onFeatureToggle('documentAccess')}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

export default ParentPortalFeaturesTab;
